import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { BottomSectionProps } from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-bottom-section';
import { ProjectsReconciliationStep } from '@/modules/common/components/templates/data-reconciliation/components/steps/projects-reconciliation-step';
import { OnboardingBottomSection } from '@/modules/onboarding/common/components/onboarding-bottom-section';
import { useOnboardingUnsavedChangesGuard } from '@/modules/onboarding/common/hooks/use-onboarding-unsaved-changes-guard';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';
import { useRootSelector } from '@/router/state/root/util/context/helpers';

export const ProjectsOnboardingStepPage: React.FC = () => {
  const state = useRootSelector(state => state);
  const { onboardingMachine } = state.children;

  const { t } = useTranslation([
    'common',
    'authenticated.onboarding.projects',
    'authenticated.onboarding.common',
  ]);

  const {
    setIsDirty,
    handleBack,
    handleClickNext,
    isUnsavedModalOpen,
    handleUnsavedModalCancel,
    handleUnsavedModalDiscard,
  } = useOnboardingUnsavedChangesGuard(onboardingMachine);

  const renderBottomSection = useCallback(
    (props: BottomSectionProps) => (
      <OnboardingBottomSection
        nextButtonProps={{
          text: t('authenticated.onboarding.projects:next'),
          onClick: props.onClickSubmit,
          isLoading: props.isLoading,
          isDisabled: props.isDisabled,
          disabledTooltip: props.nextButtonDisabledTooltip,
        }}
        backButtonProps={{
          text: t('authenticated.onboarding.projects:back'),
          onClick: handleBack,
          isDisabled: props.isLoading,
        }}
      />
    ),
    [t, handleBack]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden px-12 pb-[72px] pt-6">
      <div className="shrink-0">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('authenticated.onboarding.projects:title')}
        </h1>
        <p className="mb-2 mt-2 text-sm text-muted-foreground">
          {t('authenticated.onboarding.projects:description')}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <ProjectsReconciliationStep
          isInfiniteSizeTable={true}
          onClickNext={handleClickNext}
          bottomSection={renderBottomSection}
          onDirtyChange={setIsDirty}
        />
      </div>
      <UnsavedChangesModal
        open={isUnsavedModalOpen}
        onClose={handleUnsavedModalCancel}
        onDiscard={handleUnsavedModalDiscard}
      />
    </div>
  );
};
